import type { OrganisationJobTitle } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

// Per-org job titles. The Roster worker-edit dropdown reads the active list;
// Settings reads the full list (including archived) to manage it. The Roster
// query is invalidated alongside mutations so a rename or archive immediately
// reshapes the dropdown.

export type JobTitleDto = OrganisationJobTitle;

export interface CreateJobTitleInput {
  name: string;
}

export interface UpdateJobTitleInput {
  name?: string;
  is_active?: boolean;
}

// Active titles only — used by the Roster dropdown and any badge consumer.
export function useJobTitles() {
  const api = useApi();
  return useQuery({
    queryKey: ['job-titles', 'list', { archived: false }],
    queryFn: () => api<{ job_titles: JobTitleDto[] }>('/job-titles'),
    staleTime: 60_000,
  });
}

// All titles, including archived ones — used by the Settings management UI.
// Gated to admin on the server; non-admins quietly get active-only.
export function useAllJobTitles() {
  const api = useApi();
  return useQuery({
    queryKey: ['job-titles', 'list', { archived: true }],
    queryFn: () => api<{ job_titles: JobTitleDto[] }>('/job-titles?include_archived=true'),
    staleTime: 30_000,
  });
}

export function useJobTitleUsage(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['job-titles', 'usage', id],
    queryFn: () => api<{ worker_count: number }>(`/job-titles/${id}/usage`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useCreateJobTitle() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJobTitleInput) =>
      api<{ job_title: JobTitleDto }>('/job-titles', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-titles'] });
    },
  });
}

export function useUpdateJobTitle(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateJobTitleInput) =>
      api<{ job_title: JobTitleDto }>(`/job-titles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-titles'] });
      // A rename can change what badges render under, so blow away roster too.
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}
